<template>
  <v-container id="skills" fluid tag="section" class="dashboard-container" style="padding: 24px 96px;">

    <div class="d-flex align-center justify-space-between mb-4">
      <div class="d-flex align-center">
        <div class="sk-header-icon mr-3">
          <v-icon color="#22d3ee" size="24">mdi-medal-outline</v-icon>
        </div>
        <div>
          <div class="sk-page-title">{{ $t('skills.title') }}</div>
          <div class="sk-page-sub">{{ $t('skills.subtitle') }}</div>
        </div>
      </div>
      <v-btn outlined small class="sk-btn-manager text-none" color="#22d3ee">
        <v-icon left small>mdi-account-tie-outline</v-icon>
        {{ $t('skills.managerView') }}
      </v-btn>
    </div>

    <v-tabs
      v-model="mainTab"
      class="mb-5 sk-tabs"
      background-color="transparent"
      color="#22d3ee"
      slider-color="#22d3ee"
      slider-size="3"
    >
      <v-tab class="text-none text-body-2 font-weight-medium">
        <v-icon left small>mdi-account-group</v-icon>
        {{ $t('skills.myTeam') }}
      </v-tab>
      <v-tab class="text-none text-body-2 font-weight-medium">
        <v-icon left small>mdi-medal</v-icon>
        {{ $t('skills.competencies') }}
      </v-tab>
      <v-tab class="text-none text-body-2 font-weight-medium">
        <v-icon left small>mdi-check-decagram</v-icon>
        {{ $t('skills.validations') }}
        <v-badge
          v-if="pendingValidations.length > 0"
          :content="String(pendingValidations.length)"
          color="red"
          inline
          class="ml-1"
        />
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="mainTab">
      <v-tab-item>
        <v-row dense class="mb-5">
          <v-col v-for="(kpi, i) in teamKpis" :key="'tk'+i" cols="6" sm="3">
            <v-card elevation="0" class="pa-4 sk-kpi-card">
              <div class="d-flex align-start justify-space-between">
                <div>
                  <div class="sk-kpi-label">{{ kpi.label }}</div>
                  <div class="sk-kpi-value" :style="'color:' + kpi.color">{{ kpi.value }}</div>
                  <div class="sk-kpi-sub">{{ kpi.sub }}</div>
                </div>
                <div class="sk-kpi-icon" :style="'background:' + kpi.color + '22'">
                  <v-icon :color="kpi.color" size="20">{{ kpi.icon }}</v-icon>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <div class="d-flex mb-3 flex-wrap" style="gap: 8px">
          <v-select
            v-model="filters.time"
            :items="filterOptions.times"
            label="Todos os Times"
            outlined
            dense
            hide-details
            clearable
            prepend-inner-icon="mdi-account-group-outline"
            class="sk-filter"
          />
          <v-select
            v-model="filters.colaborador"
            :items="filterOptions.colaboradores"
            label="Todos os Colaboradores"
            outlined
            dense
            hide-details
            clearable
            prepend-inner-icon="mdi-account-outline"
            class="sk-filter"
          />
          <v-select
            v-model="sortBy"
            :items="sortOptions"
            item-text="text"
            item-value="value"
            label="Ordenar por Score Geral"
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-sort"
            class="sk-filter"
          />
          <v-select
            v-model="filters.cargo"
            :items="filterOptions.cargos"
            label="Todos os Cargos"
            outlined
            dense
            hide-details
            clearable
            prepend-inner-icon="mdi-briefcase-outline"
            class="sk-filter"
          />
        </div>

        <div class="d-flex align-center mb-2">
          <v-text-field
            v-model="teamSearch"
            placeholder="Buscar colaborador..."
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            class="flex-grow-1 mr-3"
          />
          <v-btn-toggle v-model="teamViewMode" mandatory dense class="ml-2">
            <v-btn small value="grid" class="px-2">
              <v-icon size="20">mdi-view-grid-outline</v-icon>
            </v-btn>
            <v-btn small value="list" class="px-2">
              <v-icon size="20">mdi-format-list-bulleted</v-icon>
            </v-btn>
          </v-btn-toggle>
        </div>

        <div class="sk-count-label mb-4">{{ filteredTeamMembers.length }} colaborador{{ filteredTeamMembers.length !== 1 ? 'es' : '' }}</div>

        <v-row v-if="teamViewMode === 'grid'" dense>
          <v-col
            v-for="member in filteredTeamMembers"
            :key="member.id"
            cols="12"
            sm="6"
            lg="4"
          >
            <v-card elevation="0" class="sk-member-card pa-4" height="100%">
              <div class="d-flex align-center mb-3">
                <v-avatar :color="member.avatarUrl ? '' : member.avatarColor" size="44" class="mr-3 sk-avatar">
                  <v-img v-if="member.avatarUrl" :src="member.avatarUrl" />
                  <span v-else class="white--text text-body-2 font-weight-bold">{{ member.initials }}</span>
                </v-avatar>
                <div class="flex-grow-1">
                  <div class="sk-member-name">{{ member.name }}</div>
                  <div class="sk-member-role">{{ member.role }}</div>
                </div>
                <v-chip
                  x-small
                  :color="seniorityColor(member.seniority)"
                  dark
                >
                  {{ member.seniority }}
                </v-chip>
              </div>

              <div v-for="dim in member.dimensions" :key="dim.name" class="mb-2">
                <div class="d-flex justify-space-between align-center mb-1">
                  <span class="sk-dim-label">{{ dim.name }}</span>
                  <span class="sk-dim-score font-weight-bold" :style="'color:' + dim.color">
                    {{ dim.score.toFixed(1) }}
                  </span>
                </div>
                <v-progress-linear
                  :value="dim.score * 20"
                  :color="dim.color"
                  background-color="rgba(128,128,128,0.15)"
                  height="5"
                  rounded
                />
              </div>

              <v-divider class="sk-divider my-3" />

              <div class="d-flex justify-space-between align-center">
                <div>
                  <div class="sk-dim-label">Score geral</div>
                  <div v-if="member.pendingBadges > 0" class="d-flex align-center mt-1">
                    <v-icon size="14" color="#f59e0b" class="mr-1">mdi-clock-outline</v-icon>
                    <span class="sk-status-text" style="color:#f59e0b">Pendente</span>
                  </div>
                  <div v-else class="d-flex align-center mt-1">
                    <v-icon size="14" color="#4ade80" class="mr-1">mdi-check-circle-outline</v-icon>
                    <span class="sk-status-text" style="color:#4ade80">Validado</span>
                  </div>
                </div>
                <v-progress-circular
                  :value="member.overallScore * 20"
                  :color="getScoreColor(member.overallScore)"
                  size="52"
                  width="4"
                >
                  <span class="sk-score-val" :style="'color:' + getScoreColor(member.overallScore)">{{ member.overallScore.toFixed(1) }}</span>
                </v-progress-circular>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <div v-if="teamViewMode === 'list'">
          <v-card
            v-for="member in filteredTeamMembers"
            :key="'list-' + member.id"
            elevation="0"
            class="sk-member-card mb-2"
          >
            <v-card-text class="pa-3">
              <v-row align="center" dense>
                <v-col cols="12" md="3">
                  <div class="d-flex align-center">
                    <v-avatar :color="member.avatarUrl ? '' : member.avatarColor" size="36" class="mr-2 sk-avatar">
                      <v-img v-if="member.avatarUrl" :src="member.avatarUrl" />
                      <span v-else class="white--text text-caption font-weight-bold">{{ member.initials }}</span>
                    </v-avatar>
                    <div>
                      <div class="sk-member-name">{{ member.name }}</div>
                      <div class="sk-member-role">{{ member.role }}</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="6" md="1">
                  <v-chip x-small :color="seniorityColor(member.seniority)" dark>
                    {{ member.seniority }}
                  </v-chip>
                </v-col>
                <v-col cols="6" md="2" class="text-center">
                  <span class="sk-dim-label d-block">Score</span>
                  <span class="font-weight-bold" :style="'color:' + getScoreColor(member.overallScore)">
                    {{ member.overallScore.toFixed(1) }}
                  </span>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="d-flex" style="gap: 4px">
                    <div v-for="dim in member.dimensions" :key="dim.name" class="flex-grow-1">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-progress-linear
                            v-on="on"
                            :value="dim.score * 20"
                            :color="dim.color"
                            height="4"
                            rounded
                          />
                        </template>
                        <span>{{ dim.name }}: {{ dim.score.toFixed(1) }}</span>
                      </v-tooltip>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="2" class="text-right">
                  <v-btn
                    v-if="member.pendingBadges > 0"
                    x-small
                    color="#22d3ee"
                    dark
                    rounded
                    depressed
                    class="text-none"
                    @click="openValidationForMember(member)"
                  >
                    Confirmar ({{ member.pendingBadges }})
                  </v-btn>
                  <span v-else class="sk-dim-label">{{ member.xp }} XP</span>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>

        <v-card v-if="filteredTeamMembers.length === 0 && !loading" outlined rounded="lg" class="pa-8 text-center">
          <v-icon size="64" color="grey lighten-1">mdi-account-group-outline</v-icon>
          <h3 class="text-h6 grey--text mt-4">Nenhum colaborador encontrado</h3>
          <p class="text-body-2 grey--text">Ajuste os filtros para visualizar membros da equipe.</p>
        </v-card>
      </v-tab-item>

      <v-tab-item>
        <v-row dense class="mb-5">
          <v-col v-for="(kpi, i) in competenciaKpis" :key="'ck'+i" cols="6" sm="3">
            <v-card elevation="0" class="pa-4 sk-kpi-card">
              <div class="d-flex align-start justify-space-between">
                <div>
                  <div class="sk-kpi-label">{{ kpi.label }}</div>
                  <div class="sk-kpi-value" :style="'color:' + kpi.color">{{ kpi.value }}</div>
                  <div class="sk-kpi-sub">{{ kpi.sub }}</div>
                </div>
                <div class="sk-kpi-icon" :style="'background:' + kpi.color + '22'">
                  <v-icon :color="kpi.color" size="20">{{ kpi.icon }}</v-icon>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mb-5">
          <v-col cols="12" md="7">
            <v-card class="sk-kpi-card elevation-0 pa-5" style="height:100%;">
              <div class="sk-chart-title">Mapa de Competências do Time</div>
              <div class="sk-chart-sub mb-3">Média da equipe por competência (0–5)</div>
              <div style="position:relative; height:260px;">
                <canvas ref="radarChart"></canvas>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="5">
            <v-card class="sk-kpi-card elevation-0 pa-5" style="height:100%;">
              <div class="sk-chart-title" style="color:#22d3ee;">Médias por Competência</div>
              <div class="sk-chart-sub mb-4">Desempenho consolidado da equipe</div>
              <div v-for="dim in dimensionAveragesList" :key="dim.name" class="mb-4">
                <div class="d-flex align-center justify-space-between mb-1">
                  <span class="sk-dim-label" style="font-size:13px;">{{ dim.name }}</span>
                  <span class="font-weight-bold" style="font-size:13px;" :style="{ color: dim.color }">{{ dim.avg }}</span>
                </div>
                <v-progress-linear
                  :value="dim.avg * 20"
                  :color="dim.color"
                  background-color="rgba(128,128,128,0.12)"
                  height="7"
                  rounded
                />
              </div>
            </v-card>
          </v-col>
        </v-row>

        <div class="d-flex flex-wrap mb-4" style="gap: 8px">
          <v-chip
            v-for="(dim, idx) in dimensionFilters"
            :key="idx"
            :color="compDimensionFilter === dim.value ? '#22d3ee' : ''"
            :dark="compDimensionFilter === dim.value"
            :outlined="compDimensionFilter !== dim.value"
            small
            class="text-none"
            @click="compDimensionFilter = dim.value"
          >
            <v-icon v-if="dim.icon" left small>{{ dim.icon }}</v-icon>
            {{ dim.label }}
          </v-chip>
        </div>

        <v-text-field
          v-model="compSearch"
          placeholder="Buscar competência..."
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          class="mb-4"
        />

        <v-card
          v-for="comp in filteredCompetencias"
          :key="comp.id"
          outlined
          rounded="lg"
          class="comp-card mb-3"
        >
          <v-card-text class="pa-4">
            <v-row align="center" dense>
              <v-col cols="12" md="3">
                <div class="d-flex align-center">
                  <v-avatar :color="getDimensionColor(comp.dimension) + '20'" size="36" class="mr-3">
                    <v-icon :color="getDimensionColor(comp.dimension)" size="18">
                      {{ getDimensionIcon(comp.dimension) }}
                    </v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-subtitle-2 font-weight-bold">{{ comp.name }}</div>
                    <v-chip x-small :color="getDimensionColor(comp.dimension)" dark class="mt-1">
                      {{ comp.dimension }}
                    </v-chip>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <v-progress-linear
                  :value="(comp.currentLevel / comp.maxLevel) * 100"
                  :color="getStatusColor(comp.status)"
                  background-color="#F5F5F5"
                  height="8"
                  rounded
                />
              </v-col>
              <v-col cols="6" md="2" class="text-center">
                <span class="text-h6 font-weight-bold" :style="'color:' + getStatusColor(comp.status)">
                  {{ comp.currentLevel }}
                </span>
                <span class="text-caption grey--text">/{{ comp.maxLevel }}</span>
              </v-col>
              <v-col cols="6" md="3" class="text-right">
                <v-chip
                  small
                  :color="getStatusChipColor(comp.status)"
                  :dark="comp.status === 'meta_atingida'"
                  :outlined="comp.status !== 'meta_atingida'"
                  class="font-weight-medium"
                >
                  <v-icon left x-small>{{ getStatusIcon(comp.status) }}</v-icon>
                  {{ getStatusLabel(comp.status) }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card v-if="filteredCompetencias.length === 0 && !loading" outlined rounded="lg" class="pa-8 text-center">
          <v-icon size="64" color="grey lighten-1">mdi-medal-outline</v-icon>
          <h3 class="text-h6 grey--text mt-4">Nenhuma competência encontrada</h3>
          <p class="text-body-2 grey--text">Ajuste os filtros de dimensão ou busca.</p>
        </v-card>
      </v-tab-item>

      <v-tab-item>
        <v-row dense class="mb-5">
          <v-col v-for="(kpi, i) in validacaoKpis" :key="'vk'+i" cols="6" sm="3">
            <v-card elevation="0" class="pa-4 sk-kpi-card">
              <div class="d-flex align-start justify-space-between">
                <div>
                  <div class="sk-kpi-label">{{ kpi.label }}</div>
                  <div class="sk-kpi-value" :style="'color:' + kpi.color">{{ kpi.value }}</div>
                  <div class="sk-kpi-sub">{{ kpi.sub }}</div>
                </div>
                <div class="sk-kpi-icon" :style="'background:' + kpi.color + '22'">
                  <v-icon :color="kpi.color" size="20">{{ kpi.icon }}</v-icon>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <div class="text-subtitle-1 font-weight-bold mb-3">
          <v-icon small class="mr-1" color="#22d3ee">mdi-clock-outline</v-icon>
          Validações Pendentes
        </div>

        <v-card
          v-for="val in pendingValidations"
          :key="'pv-' + val.id"
          outlined
          rounded="lg"
          class="validation-card mb-3"
        >
          <v-card-text class="pa-4">
            <v-row align="center" dense>
              <v-col cols="12" md="3">
                <div class="d-flex align-center">
                  <v-avatar :color="val.avatarColor" size="36" class="mr-2">
                    <span class="white--text text-caption font-weight-bold">{{ val.initials }}</span>
                  </v-avatar>
                  <div>
                    <div class="text-body-2 font-weight-bold">{{ val.userName }}</div>
                    <div class="text-caption grey--text">{{ val.userRole }}</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="6" md="2">
                <v-chip x-small :color="getDimensionColor(val.dimension)" dark>
                  {{ val.competency }}
                </v-chip>
              </v-col>
              <v-col cols="6" md="2" class="text-center">
                <div class="d-flex align-center justify-center">
                  <span class="text-body-2 font-weight-bold grey--text">{{ val.fromLevel }}</span>
                  <v-icon small class="mx-1" color="grey">mdi-arrow-right</v-icon>
                  <span class="text-body-2 font-weight-bold" style="color: #22d3ee">{{ val.toLevel }}</span>
                </div>
              </v-col>
              <v-col cols="6" md="2" class="text-center">
                <div>
                  <span class="text-caption grey--text">Confiança</span>
                  <v-progress-linear
                    :value="val.confidence"
                    :color="val.confidence >= 80 ? '#4ade80' : val.confidence >= 50 ? '#f59e0b' : '#f87171'"
                    height="6"
                    rounded
                    class="mt-1"
                  />
                  <span class="text-caption font-weight-medium">{{ val.confidence }}%</span>
                </div>
              </v-col>
              <v-col cols="6" md="3" class="text-right">
                <v-btn
                  small
                  rounded
                  depressed
                  color="#4ade80"
                  dark
                  class="text-none mr-2"
                  :loading="val.approving"
                  @click="approveValidation(val)"
                >
                  <v-icon left small>mdi-check</v-icon>
                  Aprovar
                </v-btn>
                <v-btn
                  small
                  rounded
                  depressed
                  outlined
                  color="#f87171"
                  class="text-none"
                  :loading="val.rejecting"
                  @click="rejectValidation(val)"
                >
                  <v-icon left small>mdi-close</v-icon>
                  Rejeitar
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card v-if="pendingValidations.length === 0" outlined rounded="lg" class="pa-6 text-center mb-6">
          <v-icon size="48" color="grey lighten-1">mdi-check-all</v-icon>
          <p class="text-body-2 grey--text mt-2 mb-0">Nenhuma validação pendente</p>
        </v-card>

        <div class="text-subtitle-1 font-weight-bold mb-3 mt-6">
          <v-icon small class="mr-1" color="#4ade80">mdi-check-circle</v-icon>
          Confirmadas este mês
        </div>

        <v-card
          v-for="val in confirmedValidations"
          :key="'cv-' + val.id"
          outlined
          rounded="lg"
          class="validation-card confirmed mb-2"
        >
          <v-card-text class="pa-3">
            <v-row align="center" dense>
              <v-col cols="12" md="3">
                <div class="d-flex align-center">
                  <v-avatar :color="val.avatarColor" size="32" class="mr-2">
                    <span class="white--text font-weight-bold" style="font-size: 11px">{{ val.initials }}</span>
                  </v-avatar>
                  <div class="text-body-2 font-weight-medium">{{ val.userName }}</div>
                </div>
              </v-col>
              <v-col cols="6" md="2">
                <v-chip x-small :color="getDimensionColor(val.dimension)" dark>
                  {{ val.competency }}
                </v-chip>
              </v-col>
              <v-col cols="6" md="2" class="text-center">
                <span class="text-caption">{{ val.fromLevel }} → {{ val.toLevel }}</span>
              </v-col>
              <v-col cols="6" md="2" class="text-center">
                <span class="text-caption grey--text">{{ val.date }}</span>
              </v-col>
              <v-col cols="6" md="3" class="text-right">
                <v-chip
                  x-small
                  :color="val.status === 'approved' ? '#4ade80' : '#f87171'"
                  dark
                >
                  <v-icon left x-small>{{ val.status === 'approved' ? 'mdi-check' : 'mdi-close' }}</v-icon>
                  {{ val.status === 'approved' ? 'Aprovada' : 'Rejeitada' }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card v-if="confirmedValidations.length === 0" outlined rounded="lg" class="pa-6 text-center">
          <v-icon size="48" color="grey lighten-1">mdi-calendar-blank</v-icon>
          <p class="text-body-2 grey--text mt-2 mb-0">Nenhuma validação confirmada este mês</p>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <v-card v-if="loading" outlined rounded="lg" class="pa-8 text-center">
      <v-progress-circular indeterminate color="#22d3ee" size="48" />
      <p class="text-body-2 grey--text mt-4">Carregando dados...</p>
    </v-card>

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
      top
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Fechar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { SkillsService } from '@/services'

export default {
  name: 'Skills',

  mixins: [
    require('@/mixins/globalViewMode').default,
  ],

  data: function () {
    return {
      loading: false,
      mainTab: 0,
      teamViewMode: 'grid',
      sortBy: 'score',
      sortOptions: [
        { text: 'Ordenar por Score Geral', value: 'score' },
        { text: 'Ordenar por Nome', value: 'name' },
        { text: 'Ordenar por Evolução', value: 'evolution' },
      ],
      teamSearch: '',
      compSearch: '',
      compDimensionFilter: 'all',

      // Filters for team tab
      filters: {
        time: null,
        colaborador: null,
        competencia: null,
        cargo: null,
      },

      filterOptions: {
        times: ['Squad Alpha', 'Squad Beta', 'Squad Gamma', 'Platform'],
        colaboradores: [],
        competencias: ['Liderança', 'Produtividade', 'Clean Code', 'Testes', 'Comunicação', 'Mentoria', 'Arquitetura', 'DevOps'],
        cargos: ['Desenvolvedor Frontend', 'Desenvolvedor Backend', 'Desenvolvedor Fullstack', 'QA Engineer', 'Tech Lead', 'DevOps Engineer'],
      },

      // Dimension filters for competencias tab
      dimensionFilters: [
        { label: 'Todas', value: 'all', icon: 'mdi-view-grid' },
        { label: 'Entrega', value: 'Entrega', icon: 'mdi-cube-send' },
        { label: 'Qualidade', value: 'Qualidade', icon: 'mdi-check-decagram' },
        { label: 'Colaboração', value: 'Colaboração', icon: 'mdi-account-group' },
        { label: 'Ev. Técnica', value: 'Evolução Técnica', icon: 'mdi-rocket-launch' },
      ],

      // Team members — populated from API (GET /api/users/my-team)
      teamMembers: [],

      // Avatar color palette for team members
      avatarColors: [
        '#7C4DFF', '#FF5722', '#E91E63', '#009688',
        '#FF9800', '#3F51B5', '#00BCD4', '#795548',
        '#4CAF50', '#F44336', '#2196F3', '#9C27B0',
      ],

      // Competências — populated from API (GET /api/skills/manager/competencias)
      competencias: [],

      // Validações pendentes — populated from API (GET /api/skills/manager/validations/pending)
      pendingValidations: [],

      // Validações confirmadas — populated from API (GET /api/skills/manager/validations/confirmed)
      confirmedValidations: [],

      // KPIs from API (fallback to computed if null)
      teamKpisFromApi: null,
      compKpisFromApi: null,
      valKpisFromApi: null,

      // Radar chart
      radarChartInstance: null,

      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: '#22d3ee',
    }
  },

  computed: {
    currentUser: function () {
      try {
        return JSON.parse(localStorage.getItem('user') || '{}')
      } catch (e) {
        return {}
      }
    },

    // ——— KPIs: Minha equipe (API first, then computed fallback) ———
    teamKpis: function () {
      if (this.teamKpisFromApi) {
        var k = this.teamKpisFromApi
        return [
          { label: 'Score médio', value: k.avgScore != null ? Number(k.avgScore).toFixed(1) : '0.0', icon: 'mdi-trending-up', color: '#22d3ee', sub: 'média geral da equipe' },
          { label: 'Validações pendentes', value: k.pendingValidations != null ? k.pendingValidations : 0, icon: 'mdi-clipboard-text-outline', color: '#a855f7', sub: 'aguardando aprovação' },
          { label: 'Gaps críticos', value: k.criticalGaps != null ? k.criticalGaps : 0, icon: 'mdi-alert-outline', color: '#f87171', sub: 'competências abaixo de 2,5' },
          { label: 'Evolução', value: k.oneOnOnesThisWeek != null ? k.oneOnOnesThisWeek : 0, icon: 'mdi-chart-line', color: '#4ade80', sub: 'esta semana' },
        ]
      }
      // Fallback: compute from local data
      var members = this.teamMembers
      var avgScore = members.length
        ? (members.reduce(function (s, m) { return s + m.overallScore }, 0) / members.length).toFixed(1)
        : '0.0'
      var totalPending = members.reduce(function (s, m) { return s + m.pendingBadges }, 0)
      var gaps = this.competencias.filter(function (c) { return c.status === 'gap_critico' }).length
      return [
        { label: 'Score médio', value: avgScore, icon: 'mdi-trending-up', color: '#22d3ee', sub: 'média geral da equipe' },
        { label: 'Validações pendentes', value: totalPending, icon: 'mdi-clipboard-text-outline', color: '#a855f7', sub: 'aguardando aprovação' },
        { label: 'Gaps críticos', value: gaps, icon: 'mdi-alert-outline', color: '#f87171', sub: 'competências abaixo de 2,5' },
        { label: 'Evolução', value: 0, icon: 'mdi-chart-line', color: '#4ade80', sub: 'esta semana' },
      ]
    },

    // ——— KPIs: Competências (API first, then computed fallback) ———
    competenciaKpis: function () {
      if (this.compKpisFromApi) {
        var k = this.compKpisFromApi
        return [
          { label: 'Nível médio', value: k.avgLevel != null ? Number(k.avgLevel).toFixed(1) : '0', icon: 'mdi-trending-up', color: '#22d3ee', sub: 'média das competências' },
          { label: 'Gaps críticos', value: k.criticalGaps != null ? k.criticalGaps : 0, icon: 'mdi-alert-circle', color: '#f87171', sub: 'abaixo do esperado' },
          { label: 'Mais evoluída', value: k.topCompetency || '-', icon: 'mdi-star', color: '#f59e0b', sub: 'destaque da equipe' },
          { label: 'Cobertura', value: k.coverage != null ? k.coverage + '%' : '0%', icon: 'mdi-check-all', color: '#4ade80', sub: 'metas atingidas' },
        ]
      }
      // Fallback: compute from local data
      var comps = this.competencias
      var avgLevel = comps.length
        ? (comps.reduce(function (s, c) { return s + c.currentLevel }, 0) / comps.length).toFixed(1)
        : '0'
      var gaps = comps.filter(function (c) { return c.status === 'gap_critico' }).length
      var sorted = comps.slice().sort(function (a, b) { return b.currentLevel - a.currentLevel })
      var topComp = sorted.length ? sorted[0].name : '-'
      var covered = comps.filter(function (c) { return c.currentLevel >= 4 }).length
      var coverage = comps.length ? Math.round((covered / comps.length) * 100) + '%' : '0%'
      return [
        { label: 'Nível médio', value: avgLevel, icon: 'mdi-trending-up', color: '#22d3ee', sub: 'média das competências' },
        { label: 'Gaps críticos', value: gaps, icon: 'mdi-alert-circle', color: '#f87171', sub: 'abaixo do esperado' },
        { label: 'Mais evoluída', value: topComp, icon: 'mdi-star', color: '#f59e0b', sub: 'destaque da equipe' },
        { label: 'Cobertura', value: coverage, icon: 'mdi-check-all', color: '#4ade80', sub: 'metas atingidas' },
      ]
    },

    // ——— KPIs: Validações (API first, then computed fallback) ———
    validacaoKpis: function () {
      if (this.valKpisFromApi) {
        var k = this.valKpisFromApi
        return [
          { label: 'Pendentes', value: k.pending != null ? k.pending : this.pendingValidations.length, icon: 'mdi-clock-outline', color: '#22d3ee', sub: 'aguardando revisão' },
          { label: 'Confirmadas no mês', value: k.confirmedThisMonth != null ? k.confirmedThisMonth : this.confirmedValidations.length, icon: 'mdi-check-circle', color: '#4ade80', sub: 'aprovadas' },
          { label: 'Tempo médio resposta', value: k.avgResponseTime || '-', icon: 'mdi-timer-sand', color: '#a855f7', sub: 'dias em média' },
          { label: 'Taxa de confirmação', value: k.confirmationRate != null ? k.confirmationRate + '%' : '0%', icon: 'mdi-percent', color: '#f59e0b', sub: 'esta semana' },
        ]
      }
      // Fallback: compute from local data
      var pending = this.pendingValidations.length
      var confirmed = this.confirmedValidations.length
      var approved = this.confirmedValidations.filter(function (v) { return v.status === 'approved' }).length
      var rate = confirmed > 0 ? Math.round((approved / confirmed) * 100) + '%' : '0%'
      return [
        { label: 'Pendentes', value: pending, icon: 'mdi-clock-outline', color: '#22d3ee', sub: 'aguardando revisão' },
        { label: 'Confirmadas no mês', value: confirmed, icon: 'mdi-check-circle', color: '#4ade80', sub: 'aprovadas' },
        { label: 'Tempo médio resposta', value: '-', icon: 'mdi-timer-sand', color: '#a855f7', sub: 'dias em média' },
        { label: 'Taxa de confirmação', value: rate, icon: 'mdi-percent', color: '#f59e0b', sub: 'esta semana' },
      ]
    },

    // ——— Team filtering ———
    filteredTeamMembers: function () {
      var self = this
      var list = this.teamMembers.slice()

      if (self.filters.time) {
        list = list.filter(function (m) {
          return m.teamNames.indexOf(self.filters.time) >= 0
        })
      }
      if (self.filters.colaborador) {
        list = list.filter(function (m) { return m.name === self.filters.colaborador })
      }
      if (self.filters.cargo) {
        list = list.filter(function (m) { return m.role === self.filters.cargo })
      }
      if (self.teamSearch) {
        var q = self.teamSearch.toLowerCase()
        list = list.filter(function (m) {
          return m.name.toLowerCase().indexOf(q) >= 0 || m.role.toLowerCase().indexOf(q) >= 0
        })
      }
      return list
    },

    filteredTeamGroups: function () {
      var members = this.filteredTeamMembers
      var groups = [
        { level: 'Sênior', color: '#4ade80', order: 1 },
        { level: 'Pleno', color: '#22d3ee', order: 2 },
        { level: 'Júnior', color: '#f59e0b', order: 3 },
      ]
      return groups
        .map(function (g) {
          return {
            level: g.level,
            color: g.color,
            members: members.filter(function (m) { return m.seniority === g.level }),
          }
        })
        .filter(function (g) { return g.members.length > 0 })
    },

    // ——— Competencias filtering ———
    filteredCompetencias: function () {
      var self = this
      var list = this.competencias.slice()

      if (self.compDimensionFilter !== 'all') {
        list = list.filter(function (c) { return c.dimension === self.compDimensionFilter })
      }
      if (self.compSearch) {
        var q = self.compSearch.toLowerCase()
        list = list.filter(function (c) {
          return c.name.toLowerCase().indexOf(q) >= 0 || c.dimension.toLowerCase().indexOf(q) >= 0
        })
      }
      return list
    },

    // Averages per dimension from teamMembers for the radar + bar chart
    dimensionAveragesList: function () {
      var members = this.teamMembers
      var dims = [
        { name: 'Entrega',      key: 'Entrega',      color: '#22d3ee' },
        { name: 'Qualidade',    key: 'Qualidade',    color: '#4ade80' },
        { name: 'Colaboração',  key: 'Colaboração',  color: '#a855f7' },
        { name: 'Ex. Técnica',  key: 'Ev. Técnica',  color: '#f59e0b' },
      ]
      if (!members.length) {
        return dims.map(function (d) { return { name: d.name, avg: 0, color: d.color } })
      }
      return dims.map(function (d) {
        var sum = 0
        var count = 0
        members.forEach(function (m) {
          m.dimensions.forEach(function (dim) {
            if (dim.name === d.key) { sum += dim.score; count++ }
          })
        })
        var avg = count > 0 ? Math.round((sum / count) * 10) / 10 : 0
        return { name: d.name, avg: avg, color: d.color }
      })
    },
  },

  watch: {
    'filters.time': function (newVal) {
      // Cascade: update colaboradores based on selected team
      var source = newVal
        ? this.teamMembers.filter(function (m) { return m.teamNames.indexOf(newVal) >= 0 })
        : this.teamMembers
      this.filterOptions.colaboradores = source.map(function (m) { return m.name })
      // Reset colaborador if no longer available
      if (this.filters.colaborador && !source.find(function (m) { return m.name === this.filters.colaborador }.bind(this))) {
        this.filters.colaborador = null
      }
      // Cascade: also update cargos based on selected team
      var cargos = []
      source.forEach(function (m) {
        if (m.role && cargos.indexOf(m.role) === -1) cargos.push(m.role)
      })
      this.filterOptions.cargos = cargos.length > 0 ? cargos : this.teamMembers.map(function (m) { return m.role }).filter(function (r, i, a) { return r && a.indexOf(r) === i })
      // Reset cargo if no longer available
      if (this.filters.cargo && this.filterOptions.cargos.indexOf(this.filters.cargo) === -1) {
        this.filters.cargo = null
      }
    },

    mainTab: function (newVal) {
      var self = this
      if (newVal === 1) {
        self.$nextTick(function () { self.renderRadarChart() })
      }
    },

    teamMembers: function () {
      var self = this
      if (self.mainTab === 1) {
        self.$nextTick(function () { self.renderRadarChart() })
      }
    },
  },

  mounted: function () {
    this.loadData()
  },

  beforeDestroy: function () {
    if (this.radarChartInstance) {
      this.radarChartInstance.destroy()
      this.radarChartInstance = null
    }
  },

  methods: {
    renderRadarChart: function () {
      var canvas = this.$refs.radarChart
      if (!canvas) return
      var Chart = require('chart.js')
      if (this.radarChartInstance) {
        this.radarChartInstance.destroy()
        this.radarChartInstance = null
      }
      var avgs = this.dimensionAveragesList
      var isDark = this.$vuetify.theme.dark
      var gridColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'
      var labelColor = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)'
      this.radarChartInstance = new Chart(canvas.getContext('2d'), {
        type: 'radar',
        data: {
          labels: avgs.map(function (d) { return d.name }),
          datasets: [{
            label: 'Média',
            data: avgs.map(function (d) { return d.avg }),
            backgroundColor: 'rgba(34,211,238,0.15)',
            borderColor: '#22d3ee',
            borderWidth: 2,
            pointBackgroundColor: '#22d3ee',
            pointBorderColor: '#22d3ee',
            pointRadius: 4,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scale: {
            ticks: {
              beginAtZero: true,
              max: 5,
              min: 0,
              stepSize: 1,
              fontColor: labelColor,
              backdropColor: 'transparent',
            },
            gridLines: { color: gridColor },
            angleLines: { color: gridColor },
            pointLabels: { fontColor: labelColor, fontSize: 12 },
          },
          tooltips: {
            callbacks: {
              label: function (item) { return ' ' + item.yLabel.toFixed(1) },
            },
          },
        },
      })
    },

    async loadData () {
      this.loading = true
      try {
        // Load all data in parallel: team, competencias, validations pending/confirmed, 3 KPIs
        var results = await Promise.allSettled([
          SkillsService.getManagerTeamMembers(),         // 0
          SkillsService.getManagerCompetencias(),         // 1
          SkillsService.getPendingValidations(),          // 2
          SkillsService.getConfirmedValidations(),        // 3
          SkillsService.getTeamKpis(),                    // 4
          SkillsService.getCompetenciasKpis(),            // 5
          SkillsService.getValidationsKpis(),             // 6
        ])

        // --- 0: Team members ---
        var teamResult = results[0]
        if (teamResult.status === 'fulfilled') {
          var apiTeam = teamResult.value.data || []
          if (apiTeam.length > 0) {
            this.teamMembers = this.mapTeamMembers(apiTeam)
            console.log('✅ Equipe carregada via API:', this.teamMembers.length, 'membros')
          } else {
            console.log('ℹ️ API retornou equipe vazia, usando mock')
            this.loadMockTeamMembers()
          }
        } else {
          console.warn('⚠️ Erro ao carregar equipe, usando mock:', teamResult.reason)
          this.loadMockTeamMembers()
        }

        // Populate colaboradores filter from loaded team
        this.filterOptions.colaboradores = this.teamMembers.map(function (m) { return m.name })

        // Populate times filter dynamically from teamNames arrays
        var teams = []
        this.teamMembers.forEach(function (m) {
          m.teamNames.forEach(function (t) {
            if (t && teams.indexOf(t) === -1) teams.push(t)
          })
        })
        if (teams.length > 0) this.filterOptions.times = teams

        // Populate cargos filter dynamically
        var cargos = []
        this.teamMembers.forEach(function (m) {
          if (m.role && cargos.indexOf(m.role) === -1) cargos.push(m.role)
        })
        if (cargos.length > 0) this.filterOptions.cargos = cargos

        // --- 1: Competências ---
        var compResult = results[1]
        if (compResult.status === 'fulfilled') {
          var apiComps = compResult.value.data || []
          if (apiComps.length > 0) {
            this.competencias = apiComps.map(function (c) {
              return {
                id: c.id,
                name: c.name,
                dimension: c.dimension,
                currentLevel: c.currentLevel != null ? c.currentLevel : 0,
                maxLevel: c.maxLevel != null ? c.maxLevel : 8,
                status: c.status || 'atencao',
              }
            })
            console.log('✅ Competências carregadas via API:', this.competencias.length)
          } else {
            console.log('ℹ️ API retornou competências vazia')
            this.loadMockCompetencias()
          }
        } else {
          console.warn('⚠️ Erro ao carregar competências, usando mock:', compResult.reason)
          this.loadMockCompetencias()
        }

        // Populate competencias filter dynamically
        var compNames = []
        this.competencias.forEach(function (c) {
          if (c.name && compNames.indexOf(c.name) === -1) compNames.push(c.name)
        })
        if (compNames.length > 0) this.filterOptions.competencias = compNames

        // --- 2: Pending Validations ---
        var pendingResult = results[2]
        if (pendingResult.status === 'fulfilled') {
          var apiPending = pendingResult.value.data || []
          this.pendingValidations = this.mapPendingValidations(apiPending)
          console.log('✅ Validações pendentes carregadas:', this.pendingValidations.length)
        } else {
          console.warn('⚠️ Erro ao carregar validações pendentes:', pendingResult.reason)
        }

        // --- 3: Confirmed Validations ---
        var confirmedResult = results[3]
        if (confirmedResult.status === 'fulfilled') {
          var apiConfirmed = confirmedResult.value.data || []
          this.confirmedValidations = this.mapConfirmedValidations(apiConfirmed)
          console.log('✅ Validações confirmadas carregadas:', this.confirmedValidations.length)
        } else {
          console.warn('⚠️ Erro ao carregar validações confirmadas:', confirmedResult.reason)
        }

        // pendingBadges already set by API (pendingValidations field)

        // --- 4: Team KPIs ---
        var teamKpiResult = results[4]
        if (teamKpiResult.status === 'fulfilled' && teamKpiResult.value.data) {
          this.teamKpisFromApi = teamKpiResult.value.data
          console.log('✅ Team KPIs carregados via API')
        }

        // --- 5: Competencias KPIs ---
        var compKpiResult = results[5]
        if (compKpiResult.status === 'fulfilled' && compKpiResult.value.data) {
          this.compKpisFromApi = compKpiResult.value.data
          console.log('✅ Competências KPIs carregados via API')
        }

        // --- 6: Validations KPIs ---
        var valKpiResult = results[6]
        if (valKpiResult.status === 'fulfilled' && valKpiResult.value.data) {
          this.valKpisFromApi = valKpiResult.value.data
          console.log('✅ Validações KPIs carregados via API')
        }
      } catch (e) {
        console.error('Erro geral ao carregar dados:', e)
        if (this.teamMembers.length === 0) this.loadMockTeamMembers()
        if (this.competencias.length === 0) this.loadMockCompetencias()
      } finally {
        this.loading = false
      }
    },

    /**
     * Map API users (from /api/skills/manager/team-members) to the teamMembers view model.
     * API shape: { userId, name, email, avatarUrl, jobRoleTitle, departmentName, seniority,
     * teamIds, teamNames, entrega, qualidade, colaboracao, evolucaoTecnica,
     * overallScore, totalXp, pendingValidations, competencies }
     */
    mapTeamMembers: function (apiUsers) {
      var self = this
      return apiUsers.map(function (u, idx) {
        var name = (u.name || 'Sem nome').trim()
        var parts = name.split(' ').filter(function (p) { return p.length > 0 })
        var initials = parts.length >= 2
          ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
          : name.substring(0, 2).toUpperCase()

        var dimensions = [
          { name: 'Entrega', score: u.entrega != null ? Math.round(u.entrega * 10) / 10 : 0, color: '#22d3ee' },
          { name: 'Qualidade', score: u.qualidade != null ? Math.round(u.qualidade * 10) / 10 : 0, color: '#4ade80' },
          { name: 'Colaboração', score: u.colaboracao != null ? Math.round(u.colaboracao * 10) / 10 : 0, color: '#a855f7' },
          { name: 'Ev. Técnica', score: u.evolucaoTecnica != null ? Math.round(u.evolucaoTecnica * 10) / 10 : 0, color: '#f59e0b' },
        ]

        var teamNames = u.teamNames || []

        return {
          id: u.userId || idx,
          name: name,
          email: u.email || '',
          role: u.jobRoleTitle || '',
          seniority: u.seniority || 'Pleno',
          teamNames: teamNames,
          team: teamNames.length > 0 ? teamNames[0] : (u.departmentName || 'Sem time'),
          departmentName: u.departmentName || '',
          avatarColor: self.avatarColors[idx % self.avatarColors.length],
          avatarUrl: u.avatarUrl || null,
          initials: initials,
          dimensions: dimensions,
          overallScore: u.overallScore != null ? Math.round(u.overallScore * 10) / 10 : 0,
          xp: u.totalXp != null ? u.totalXp : 0,
          pendingBadges: u.pendingValidations != null ? u.pendingValidations : 0,
        }
      })
    },

    deriveSeniority: function (jobRoleName) {
      if (!jobRoleName) return 'Pleno'
      var lower = jobRoleName.toLowerCase()
      if (lower.indexOf('senior') >= 0 || lower.indexOf('sênior') >= 0 ||
          lower.indexOf('sr') >= 0 || lower.indexOf('lead') >= 0 ||
          lower.indexOf('principal') >= 0 || lower.indexOf('staff') >= 0) {
        return 'Sênior'
      }
      if (lower.indexOf('junior') >= 0 || lower.indexOf('júnior') >= 0 ||
          lower.indexOf('jr') >= 0 || lower.indexOf('estagiário') >= 0 ||
          lower.indexOf('intern') >= 0 || lower.indexOf('trainee') >= 0) {
        return 'Júnior'
      }
      return 'Pleno'
    },

    loadMockTeamMembers: function () {
      this.teamMembers = [
        {
          id: 1, name: 'Ana Oliveira', role: 'Desenvolvedora Frontend', seniority: 'Sênior',
          team: 'Squad Alpha', teamNames: ['Squad Alpha'], avatarColor: '#7C4DFF', initials: 'AO', avatarUrl: null,
          dimensions: [
            { name: 'Entrega', score: 4.2, color: '#22d3ee' },
            { name: 'Qualidade', score: 4.5, color: '#4ade80' },
            { name: 'Colaboração', score: 3.8, color: '#a855f7' },
            { name: 'Ev. Técnica', score: 4.0, color: '#f59e0b' },
          ],
          overallScore: 4.1, xp: 1580, pendingBadges: 2,
        },
        {
          id: 2, name: 'Carlos Lima', role: 'Desenvolvedor Backend', seniority: 'Sênior',
          team: 'Squad Alpha', teamNames: ['Squad Alpha'], avatarColor: '#FF5722', initials: 'CL', avatarUrl: null,
          dimensions: [
            { name: 'Entrega', score: 4.0, color: '#22d3ee' },
            { name: 'Qualidade', score: 4.8, color: '#4ade80' },
            { name: 'Colaboração', score: 4.2, color: '#a855f7' },
            { name: 'Ev. Técnica', score: 4.5, color: '#f59e0b' },
          ],
          overallScore: 4.4, xp: 1820, pendingBadges: 1,
        },
        {
          id: 3, name: 'Beatriz Santos', role: 'Desenvolvedora Fullstack', seniority: 'Pleno',
          team: 'Squad Beta', teamNames: ['Squad Beta'], avatarColor: '#E91E63', initials: 'BS', avatarUrl: null,
          dimensions: [
            { name: 'Entrega', score: 3.5, color: '#22d3ee' },
            { name: 'Qualidade', score: 3.8, color: '#4ade80' },
            { name: 'Colaboração', score: 4.0, color: '#a855f7' },
            { name: 'Ev. Técnica', score: 3.2, color: '#f59e0b' },
          ],
          overallScore: 3.6, xp: 980, pendingBadges: 3,
        },
        {
          id: 4, name: 'Diego Rocha', role: 'Desenvolvedor Backend', seniority: 'Pleno',
          team: 'Squad Alpha', teamNames: ['Squad Alpha'], avatarColor: '#009688', initials: 'DR', avatarUrl: null,
          dimensions: [
            { name: 'Entrega', score: 3.8, color: '#22d3ee' },
            { name: 'Qualidade', score: 3.5, color: '#4ade80' },
            { name: 'Colaboração', score: 3.0, color: '#a855f7' },
            { name: 'Ev. Técnica', score: 3.6, color: '#f59e0b' },
          ],
          overallScore: 3.5, xp: 890, pendingBadges: 0,
        },
        {
          id: 5, name: 'Fernanda Costa', role: 'QA Engineer', seniority: 'Pleno',
          team: 'Squad Beta', teamNames: ['Squad Beta'], avatarColor: '#FF9800', initials: 'FC', avatarUrl: null,
          dimensions: [
            { name: 'Entrega', score: 3.2, color: '#22d3ee' },
            { name: 'Qualidade', score: 4.2, color: '#4ade80' },
            { name: 'Colaboração', score: 3.6, color: '#a855f7' },
            { name: 'Ev. Técnica', score: 3.0, color: '#f59e0b' },
          ],
          overallScore: 3.5, xp: 870, pendingBadges: 1,
        },
        {
          id: 6, name: 'Lucas Mendes', role: 'Desenvolvedor Frontend', seniority: 'Júnior',
          team: 'Squad Gamma', teamNames: ['Squad Gamma'], avatarColor: '#3F51B5', initials: 'LM', avatarUrl: null,
          dimensions: [
            { name: 'Entrega', score: 2.5, color: '#22d3ee' },
            { name: 'Qualidade', score: 2.8, color: '#4ade80' },
            { name: 'Colaboração', score: 3.2, color: '#a855f7' },
            { name: 'Ev. Técnica', score: 2.0, color: '#f59e0b' },
          ],
          overallScore: 2.6, xp: 420, pendingBadges: 0,
        },
        {
          id: 7, name: 'Mariana Alves', role: 'Desenvolvedora Backend', seniority: 'Júnior',
          team: 'Squad Gamma', teamNames: ['Squad Gamma'], avatarColor: '#00BCD4', initials: 'MA', avatarUrl: null,
          dimensions: [
            { name: 'Entrega', score: 2.2, color: '#22d3ee' },
            { name: 'Qualidade', score: 2.5, color: '#4ade80' },
            { name: 'Colaboração', score: 3.5, color: '#a855f7' },
            { name: 'Ev. Técnica', score: 2.8, color: '#f59e0b' },
          ],
          overallScore: 2.8, xp: 350, pendingBadges: 1,
        },
        {
          id: 8, name: 'Rafael Torres', role: 'DevOps Engineer', seniority: 'Sênior',
          team: 'Platform', teamNames: ['Platform'], avatarColor: '#795548', initials: 'RT', avatarUrl: null,
          dimensions: [
            { name: 'Entrega', score: 4.5, color: '#22d3ee' },
            { name: 'Qualidade', score: 4.0, color: '#4ade80' },
            { name: 'Colaboração', score: 3.5, color: '#a855f7' },
            { name: 'Ev. Técnica', score: 4.8, color: '#f59e0b' },
          ],
          overallScore: 4.2, xp: 1650, pendingBadges: 0,
        },
      ]
    },

    /**
     * Map API pending validations to view model.
     * API shape: { id, userName, userRole, avatarUrl, competency, dimension, fromLevel, toLevel, confidence }
     */
    mapPendingValidations: function (apiList) {
      var self = this
      return apiList.map(function (v, idx) {
        var name = v.userName || 'Sem nome'
        var parts = name.split(' ')
        var initials = parts.length >= 2
          ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
          : name.substring(0, 2).toUpperCase()
        return {
          id: v.id,
          userName: name,
          userRole: v.userRole || '',
          initials: initials,
          avatarColor: self.avatarColors[idx % self.avatarColors.length],
          avatarUrl: v.avatarUrl || null,
          competency: v.competency || '',
          dimension: v.dimension || '',
          fromLevel: v.fromLevel != null ? v.fromLevel : 0,
          toLevel: v.toLevel != null ? v.toLevel : 0,
          confidence: v.confidence != null ? v.confidence : 0,
          approving: false,
          rejecting: false,
        }
      })
    },

    /**
     * Map API confirmed validations to view model.
     * API shape: { id, userName, avatarUrl, competency, dimension, fromLevel, toLevel, date, status }
     */
    mapConfirmedValidations: function (apiList) {
      var self = this
      return apiList.map(function (v, idx) {
        var name = v.userName || 'Sem nome'
        var parts = name.split(' ')
        var initials = parts.length >= 2
          ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
          : name.substring(0, 2).toUpperCase()
        // Format date if it comes as ISO string
        var dateStr = v.date || ''
        if (dateStr && dateStr.indexOf('-') >= 0) {
          try {
            var d = new Date(dateStr)
            dateStr = d.toLocaleDateString('pt-BR')
          } catch (e) { /* keep original */ }
        }
        return {
          id: v.id,
          userName: name,
          initials: initials,
          avatarColor: self.avatarColors[idx % self.avatarColors.length],
          avatarUrl: v.avatarUrl || null,
          competency: v.competency || '',
          dimension: v.dimension || '',
          fromLevel: v.fromLevel != null ? v.fromLevel : 0,
          toLevel: v.toLevel != null ? v.toLevel : 0,
          date: dateStr,
          status: v.status || 'approved',
        }
      })
    },

    /**
     * Mock fallback for competencias (when API is unavailable)
     */
    loadMockCompetencias: function () {
      this.competencias = [
        { id: 1, name: 'Liderança', dimension: 'Entrega', currentLevel: 5, maxLevel: 8, status: 'meta_atingida' },
        { id: 2, name: 'Produtividade', dimension: 'Entrega', currentLevel: 3, maxLevel: 8, status: 'atencao' },
        { id: 3, name: 'Autonomia', dimension: 'Entrega', currentLevel: 6, maxLevel: 8, status: 'meta_atingida' },
        { id: 4, name: 'Gestão de Tempo', dimension: 'Entrega', currentLevel: 2, maxLevel: 8, status: 'gap_critico' },
        { id: 5, name: 'Clean Code', dimension: 'Qualidade', currentLevel: 7, maxLevel: 8, status: 'meta_atingida' },
        { id: 6, name: 'Testes Automatizados', dimension: 'Qualidade', currentLevel: 4, maxLevel: 8, status: 'atencao' },
        { id: 7, name: 'Code Review', dimension: 'Qualidade', currentLevel: 5, maxLevel: 8, status: 'meta_atingida' },
        { id: 8, name: 'Documentação', dimension: 'Qualidade', currentLevel: 2, maxLevel: 8, status: 'gap_critico' },
        { id: 9, name: 'Comunicação', dimension: 'Colaboração', currentLevel: 6, maxLevel: 8, status: 'meta_atingida' },
        { id: 10, name: 'Mentoria', dimension: 'Colaboração', currentLevel: 4, maxLevel: 8, status: 'atencao' },
        { id: 11, name: 'Trabalho em Equipe', dimension: 'Colaboração', currentLevel: 7, maxLevel: 8, status: 'meta_atingida' },
        { id: 12, name: 'Feedback', dimension: 'Colaboração', currentLevel: 3, maxLevel: 8, status: 'atencao' },
        { id: 13, name: 'Arquitetura', dimension: 'Evolução Técnica', currentLevel: 5, maxLevel: 8, status: 'meta_atingida' },
        { id: 14, name: 'DevOps / CI-CD', dimension: 'Evolução Técnica', currentLevel: 4, maxLevel: 8, status: 'atencao' },
        { id: 15, name: 'Aprendizado Contínuo', dimension: 'Evolução Técnica', currentLevel: 6, maxLevel: 8, status: 'meta_atingida' },
        { id: 16, name: 'Inovação', dimension: 'Evolução Técnica', currentLevel: 1, maxLevel: 8, status: 'gap_critico' },
      ]
    },

    getScoreColor: function (score) {
      if (score >= 4.0) return '#4ade80'
      if (score >= 3.0) return '#22d3ee'
      if (score >= 2.0) return '#f59e0b'
      return '#f87171'
    },

    seniorityColor: function (seniority) {
      if (seniority === 'Sênior') return '#22d3ee'
      if (seniority === 'Pleno') return '#a855f7'
      return '#f59e0b'
    },

    getDimensionColor: function (dimension) {
      var map = {
        'Entrega': '#22d3ee',
        'Qualidade': '#4ade80',
        'Colaboração': '#a855f7',
        'Evolução Técnica': '#f59e0b',
      }
      return map[dimension] || '#22d3ee'
    },

    getDimensionIcon: function (dimension) {
      var map = {
        'Entrega': 'mdi-cube-send',
        'Qualidade': 'mdi-check-decagram',
        'Colaboração': 'mdi-account-group',
        'Evolução Técnica': 'mdi-rocket-launch',
      }
      return map[dimension] || 'mdi-medal'
    },

    getStatusColor: function (status) {
      var map = {
        'meta_atingida': '#4ade80',
        'atencao': '#f59e0b',
        'gap_critico': '#f87171',
      }
      return map[status] || '#9E9E9E'
    },

    getStatusChipColor: function (status) {
      var map = {
        'meta_atingida': '#4ade80',
        'atencao': '#f59e0b',
        'gap_critico': '#f87171',
      }
      return map[status] || '#9E9E9E'
    },

    getStatusIcon: function (status) {
      var map = {
        'meta_atingida': 'mdi-check-circle',
        'atencao': 'mdi-alert',
        'gap_critico': 'mdi-alert-circle',
      }
      return map[status] || 'mdi-help-circle'
    },

    getStatusLabel: function (status) {
      var map = {
        'meta_atingida': 'Meta atingida',
        'atencao': 'Atenção',
        'gap_critico': 'Gap crítico',
      }
      return map[status] || status
    },

    openValidationForMember: function (member) {
      // Switch to Validações tab and optionally filter by member
      this.mainTab = 2
      this.showSnackbar('Mostrando validações de ' + member.name, 'info')
    },

    async approveValidation (val) {
      val.approving = true
      try {
        await SkillsService.approveValidation(val.id)
        // Move from pending to confirmed
        var idx = this.pendingValidations.findIndex(function (v) { return v.id === val.id })
        if (idx >= 0) {
          this.pendingValidations.splice(idx, 1)
        }
        this.confirmedValidations.unshift({
          id: val.id,
          userName: val.userName,
          initials: val.initials,
          avatarColor: val.avatarColor,
          avatarUrl: val.avatarUrl || null,
          competency: val.competency,
          dimension: val.dimension,
          fromLevel: val.fromLevel,
          toLevel: val.toLevel,
          date: new Date().toLocaleDateString('pt-BR'),
          status: 'approved',
        })
        // Update member pending badge count
        var member = this.teamMembers.find(function (m) { return m.name === val.userName })
        if (member && member.pendingBadges > 0) {
          member.pendingBadges--
        }
        this.showSnackbar('Validação aprovada: ' + val.competency + ' de ' + val.userName, 'success')
      } catch (e) {
        console.error('Erro ao aprovar validação:', e)
        this.showSnackbar('Erro ao aprovar validação. Tente novamente.', '#f87171')
      } finally {
        val.approving = false
      }
    },

    async rejectValidation (val) {
      val.rejecting = true
      try {
        await SkillsService.rejectValidation(val.id)
        var idx = this.pendingValidations.findIndex(function (v) { return v.id === val.id })
        if (idx >= 0) {
          this.pendingValidations.splice(idx, 1)
        }
        this.confirmedValidations.unshift({
          id: val.id,
          userName: val.userName,
          initials: val.initials,
          avatarColor: val.avatarColor,
          avatarUrl: val.avatarUrl || null,
          competency: val.competency,
          dimension: val.dimension,
          fromLevel: val.fromLevel,
          toLevel: val.toLevel,
          date: new Date().toLocaleDateString('pt-BR'),
          status: 'rejected',
        })
        var member = this.teamMembers.find(function (m) { return m.name === val.userName })
        if (member && member.pendingBadges > 0) {
          member.pendingBadges--
        }
        this.showSnackbar('Validação rejeitada: ' + val.competency + ' de ' + val.userName, '#f87171')
      } catch (e) {
        console.error('Erro ao rejeitar validação:', e)
        this.showSnackbar('Erro ao rejeitar validação. Tente novamente.', '#f87171')
      } finally {
        val.rejecting = false
      }
    },

    showSnackbar: function (text, color) {
      this.snackbarText = text
      this.snackbarColor = color || 'success'
      this.snackbar = true
    },
  },
}
</script>

<style scoped>
/* Fundo Global Premium */
.dashboard-container {
  background-color: var(--bg-main, #0D0D0D) !important;
  max-width: 1440px;
  margin: 0 auto;
}

/* ── Header ──────────────────────────────── */
.sk-header-icon {
  width: 44px; height: 44px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(34,211,238,0.12); flex-shrink: 0;
}
.sk-page-title { font-size: 22px; font-weight: 700; letter-spacing: -0.01em; }
.sk-page-sub { font-size: 13px; margin-top: 2px; }
.theme--dark .sk-page-title { color: #f5f5f5; }
.theme--light .sk-page-title { color: #1D1D1F; }
.theme--dark .sk-page-sub { color: rgba(255,255,255,0.45); }
.theme--light .sk-page-sub { color: rgba(0,0,0,0.5); }

/* ── Manager button ──────────────────────── */
.sk-btn-manager { border-radius: 20px !important; font-weight: 600 !important; }
.theme--dark .sk-btn-manager { border-color: #22d3ee !important; color: #22d3ee !important; }
.theme--light .sk-btn-manager { border-color: #22d3ee !important; color: #22d3ee !important; }

/* ── Tabs ─────────────────────────────────── */
.sk-tabs ::v-deep .v-tab { min-width: auto; padding: 0 16px; letter-spacing: normal; }
.sk-tabs ::v-deep .v-tab--active { font-weight: 600; color: #22d3ee !important; }
.sk-tabs ::v-deep .v-tabs-slider { background-color: #22d3ee !important; }

/* ── KPI cards ───────────────────────────── */
.sk-kpi-card { border-radius: 12px !important; }
.theme--dark .sk-kpi-card.v-card { background: #111111 !important; border: 1px solid #1a1a1a !important; }
.theme--light .sk-kpi-card.v-card { background: #ffffff !important; border: 1px solid #f0f0f0 !important; }
.sk-kpi-label { font-size: 12px; color: #888; margin-bottom: 6px; }
.sk-kpi-value { font-size: 34px; font-weight: 700; line-height: 1.1; }
.sk-kpi-sub { font-size: 11.5px; color: #666; margin-top: 4px; }
.sk-kpi-icon {
  width: 38px; height: 38px; border-radius: 9px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}

/* ── Filters ─────────────────────────────── */
.sk-filter { max-width: 220px; flex-shrink: 0; }

/* ── Count label ─────────────────────────── */
.sk-count-label { font-size: 12.5px; }
.theme--dark .sk-count-label { color: rgba(255,255,255,0.4); }
.theme--light .sk-count-label { color: rgba(0,0,0,0.45); }

/* ── Member card ─────────────────────────── */
.sk-member-card { border-radius: 12px !important; transition: box-shadow 0.2s; }
.theme--dark .sk-member-card.v-card { background: #111111 !important; border: 1px solid #1a1a1a !important; }
.theme--light .sk-member-card.v-card { background: #ffffff !important; border: 1px solid #eeeeee !important; }
.sk-member-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.25) !important; }

.sk-avatar { border-radius: 8px !important; }

.sk-member-name { font-size: 14px; font-weight: 700; }
.theme--dark .sk-member-name { color: rgba(255,255,255,0.92); }
.theme--light .sk-member-name { color: #1D1D1F; }

.sk-member-role { font-size: 12px; }
.theme--dark .sk-member-role { color: rgba(255,255,255,0.45); }
.theme--light .sk-member-role { color: rgba(0,0,0,0.5); }

.sk-dim-label { font-size: 12px; }
.theme--dark .sk-dim-label { color: rgba(255,255,255,0.55); }
.theme--light .sk-dim-label { color: rgba(0,0,0,0.55); }

.sk-dim-score { font-size: 12.5px; }

.sk-divider { border-color: rgba(128,128,128,0.12) !important; }

.sk-status-text { font-size: 12px; font-weight: 500; }

.sk-score-val { font-size: 14px; font-weight: 700; }

/* ── Comp / Validation cards ─────────────── */
.comp-card { border-radius: 10px !important; transition: box-shadow 0.15s; }
.theme--dark .comp-card.v-card { background: #111111 !important; border: 1px solid #1a1a1a !important; }

.validation-card { border-radius: 10px !important; border-left: 3px solid #22d3ee !important; transition: box-shadow 0.15s; }
.theme--dark .validation-card.v-card { background: #111111 !important; border-top: 1px solid #1a1a1a; border-right: 1px solid #1a1a1a; border-bottom: 1px solid #1a1a1a; }
.validation-card.confirmed { border-left-color: #4ade80 !important; }

/* ── Chart cards ─────────────────────────── */
.sk-chart-title {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.theme--dark .sk-chart-title { color: rgba(255,255,255,0.92); }
.theme--light .sk-chart-title { color: #1D1D1F; }

.sk-chart-sub {
  font-size: 12px;
}
.theme--dark .sk-chart-sub { color: rgba(255,255,255,0.4); }
.theme--light .sk-chart-sub { color: rgba(0,0,0,0.45); }

/* progress bar background dark */
.theme--dark .sk-member-card ::v-deep .v-progress-linear__background { background-color: rgba(128,128,128,0.15) !important; }
</style>